import { base64UrlDecode } from '../utils/Base64UrlDecode'
import { createContext, useEffect, useMemo, useState } from 'react'
import { ensureStorageTokenEventHandler } from '../services/authEventHandlers'
import { history, routePrefixCustomer, routePrefixDashboard } from '../ApplicationRouter'
import { isRoleMatch } from '../utils/Roles'
import { Roles, RolesDashboards, RolesUsers } from '../enums/Roles'

export const authTokenKey = "KJE_NFT-authtoken";

interface IAuthObject {
  setToken: (token: string) => void;
  isAuthenticated: boolean;
  fullName: string;
  role: string;
  isInRole: (r: Roles) => boolean;
  isInRoles: (roles: Roles[]) => boolean;
  isInDashboardRole: () => boolean;
  isInCustomerRole: () => boolean;
  hasRole: () => boolean;
  signOut: () => void;
  redirectToRole: () => string;
  removeAuthToken: () => void;
  token: string;
}

export const splitToken = (token: string) => {
  if (token.length === 0) return []
  return token.split(".");
}

export const parsePayload = (tokenParts: string[]) => {
  if (tokenParts.length !== 3) return { exp: 0 }
  return JSON.parse(base64UrlDecode(tokenParts[1]));
}

// TODO: Get rid of the stupid parts, payload, token string and create 1 object containing all.
export const useAuth = (initToken: string) => {
  const [token, setTokenState] = useState(initToken);

  useEffect(() => {
    setToken(initToken);
  }, [])

  const tokenParts = useMemo(() => {
    return splitToken(token);
  }, [token]);

  const tokenPayload = useMemo(() => {
    return parsePayload(tokenParts);
  }, [tokenParts])

  const isDateValid = useMemo(() => {
    if (tokenPayload.FullName !== undefined) {
      return Math.ceil(Date.now() / 1000) <= tokenPayload.exp;
    }
    return false;
  }, [tokenPayload])

  const isAuthenticated = useMemo(
    () => {
      return isDateValid;
    },
    [isDateValid],
  );

  const fullName = useMemo(() => {
    if (tokenPayload && isAuthenticated) {
      return tokenPayload["FullName"].split(",")[0];
    }
    return "";
  }, [tokenPayload]);

  const role = useMemo(() => {
    if (tokenPayload && isAuthenticated) {
      return tokenPayload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"].toLowerCase()
    }
    return "";
  }, [tokenPayload])

  function isInRole(r: Roles) {
    return (role && r) && isRoleMatch(role, r.toLowerCase());
  }

  function isInRoles(roles: Roles[]) {
    return (roles && role) && roles.some((r: Roles) => isRoleMatch(role, r.toLowerCase()));
  }

  function removeAuthToken() {
    localStorage.removeItem(authTokenKey);
    setTokenState("");
  }

  function hasRole() {
    return role.length > 0;
  }

  function signOut() {
    removeAuthToken();
    history.push("/signout");
  }

  function redirectToRole() {
    if (hasRole()) {
      if (isInRoles(RolesUsers)) {
        return routePrefixCustomer;
      }
      else if (isInRoles(RolesDashboards)) {
        return routePrefixDashboard;
      }
    }
    return "/";
  }

  function isInDashboardRole() {
    if (isAuthenticated) {
      if (isInRole(Roles.ADMIN) || isInRole(Roles.ORGANIZATIONMANAGER)) {
        return true;
      }
    }
    return false;
  }

  function isInCustomerRole() {
    if (isAuthenticated) {
      if (isInRole(Roles.CUSTOMER)) {
        return true;
      }
    }
    return false;
  }

  function setToken(token: string) {
    ensureStorageTokenEventHandler();
    setTokenState(token);
    localStorage.setItem(authTokenKey, ""); // The event handler does not handle changes if the value being set in storage is the same as the current value.
    localStorage.setItem(authTokenKey, token);
  }

  const authObject: IAuthObject = {
    isAuthenticated,
    fullName,
    role,
    isInRole,
    isInRoles,
    isInCustomerRole,
    isInDashboardRole,
    hasRole,
    signOut,
    redirectToRole,
    setToken,
    removeAuthToken,
    token
  }

  return authObject;
}

export const AuthContext = createContext<IAuthObject>({} as IAuthObject);